import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { getTrackByFn } from '@y42/angular/util';
import { base64ToFile, ImageCroppedEvent, ImageTransform } from 'ngx-image-cropper';
import { Subject } from 'rxjs';
import { ImageCropData, RatioOption } from './image-crop-dialog.model';

@Component({
  selector: 'y42-image-crop-dialog',
  templateUrl: './image-crop-dialog.component.html',
  styleUrls: ['./image-crop-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageCropDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ImageCropData,
    private readonly dialogRef: MatDialogRef<ImageCropDialogComponent>,
  ) {
    const { ratioOptions, enableRatio, enableFlip, enablePreview, enableRotate, enableZoom, enableContain, initRatio } =
      data.options;

    if (ratioOptions) {
      this.ratioOptions = ratioOptions;
    }
    if (enableContain) {
      this.enableContain = enableContain;
    }
    if (enableRatio) {
      this.enableRatio = enableRatio;
    }
    if (enableFlip) {
      this.enableFlip = enableFlip;
    }
    if (enablePreview) {
      this.enablePreview = enablePreview;
    }
    if (enableZoom) {
      this.enableZoom = enableZoom;
    }
    if (enableRotate) {
      this.enableRotate = enableRotate;
    }
    if (initRatio) {
      this.ratio = initRatio;
      this.lockRatio = true;
    }

    this.onCrop$ = data.onCrop;
  }

  private readonly onCrop$: Subject<Blob>;

  imgHeight: number | undefined;
  imgWidth: number | undefined;

  enableRotate = true;
  enableRatio = true;
  enableZoom = true;
  enablePreview = true;
  enableFlip = true;
  enableContain = true;

  croppedImage = '';
  showCropper = false;
  canvasRotation = 0;
  rotation = 0;
  scale = 1;
  containWithinAspectRatio = true;
  transform: ImageTransform = {};
  ratio: number = 4 / 3;
  lockRatio = false;
  ratioOptions: RatioOption[] = [
    { key: '1:1', value: 1 },
    { key: '3:4', value: 3 / 4 },
    { key: '4:3', value: 4 / 3 },
    { key: '9:16', value: 9 / 16 },
    { key: '16:9', value: 16 / 9 },
    { key: '10:16', value: 10 / 16 },
    { key: '16:10', value: 16 / 10 },
  ];

  readonly trackByValueProp = getTrackByFn<RatioOption>('key');

  imageCropped(event: ImageCroppedEvent) {
    if (!event.base64) {
      return;
    }

    this.imgHeight = event.height;
    this.imgWidth = event.width;

    this.croppedImage = event.base64;
  }

  imageLoaded() {
    this.showCropper = true;
  }

  rotateLeft() {
    this.canvasRotation--;
    this.flipAfterRotate();
  }

  rotateRight() {
    this.canvasRotation++;
    this.flipAfterRotate();
  }

  private flipAfterRotate() {
    const flippedH = this.transform.flipH;
    const flippedV = this.transform.flipV;
    this.transform = {
      ...this.transform,
      flipH: flippedV,
      flipV: flippedH,
    };
  }

  flipHorizontal() {
    this.transform = {
      ...this.transform,
      flipH: !this.transform.flipH,
    };
  }

  flipVertical() {
    this.transform = {
      ...this.transform,
      flipV: !this.transform.flipV,
    };
  }

  resetImage() {
    this.scale = 1;
    this.rotation = 0;
    this.canvasRotation = 0;
    this.transform = {};
  }

  zoomOut() {
    this.scale -= 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  zoomIn() {
    this.scale += 0.1;
    this.transform = {
      ...this.transform,
      scale: this.scale,
    };
  }

  toggleLock() {
    this.lockRatio = !this.lockRatio;
  }

  changeRatio(change: MatSelectChange) {
    this.lockRatio = true;
    this.ratio = change.value;
  }

  toggleContainWithinAspectRatio() {
    this.containWithinAspectRatio = !this.containWithinAspectRatio;
  }

  updateRotation() {
    this.transform = {
      ...this.transform,
      rotate: this.rotation,
    };
  }

  crop() {
    this.onCrop$.next(base64ToFile(this.croppedImage));
    this.dialogRef.close();
  }
}
